import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Form,
  FormBuilderFields,
  FunnelStepHeader,
  FunnelWithHelper,
  SectionedFormBuilder,
  areFieldsDirty,
  buildAllFieldsFromSections,
  buildFormValuesFromFields,
  buildPayloadFromFields,
  getResolver,
} from '@folio/components';
import { isFieldVisible } from '@folio/components/forms/form-builder/conditionalVisibilityUtils';
import { FormBuilderFooter } from '@folio/components/forms/form-builder/FormBuilderFooter';
import { getArrayFieldsResolver } from '@folio/components/forms/form-builder/getZodResolverFromFields';
import { useStepperContext } from '@folio/components/layout/StepperLayout/useStepperContext';
import { useAssertedParams } from '@folio/lib';
import { useQuery } from '@tanstack/react-query';
import {
  clientWithFork,
  customerControllerGetCustomerQueryOptions,
  getNewBranchIdAfterFork,
  keyValueFieldsToObject,
  policyControllerGetPolicyQueryOptions,
  useCustomerControllerEditCustomer,
  useCustomerControllerCreateCustomerForPolicy,
  useCustomerControllerDeleteCustomerFromPolicy,
  viewSpecificControllerListPolicyCustomersQueryOptions,
} from 'korint-sdk';
import type { CreateCustomerForPolicyInputBody } from 'korint-sdk';
import type { Field, FormSectionsWithShortcuts } from 'korint-sdk/react';
import type { AfterSubmitFn } from '../buildFieldCleanup';
import { useResolvedEntitySections } from '../shared-fields/use-entity-fields';
import { isEmpty } from 'lodash';
import { shouldShowAutoFill } from '@folio/utils';
import { SectionAutoFillButton } from '@folio/components/forms/form-builder/FormBuilder';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useFieldArray,
  useForm,
  useFormState,
  useWatch,
  type Control,
  type Resolver,
  type UseFormReturn,
} from 'react-hook-form';
import { Center, Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

type Customer = {
  id?: string;
  [key: string]: unknown;
};

type CustomerFunnelModuleProps = {
  sections: FormSectionsWithShortcuts;
  path: string;
  mode?: 'stepper' | 'standalone';
  maxCustomers?: number;
  minCustomers?: number;
  title?: string;
  subtitle?: string;
  description?: string;
  role?: string;
  addCustomerButtonLabel?: string;
  customResolver?: Resolver<Record<string, unknown>>;
  afterSubmit?: AfterSubmitFn;
  helper?: React.ReactNode;
};

type RoleFieldMeta = {
  options: Array<{ value: string; label: string }>;
  isSingleOption: boolean;
} | null;

function CustomerCard({
  index,
  control,
  roleField,
  productId,
  path,
  description,
  canRemove,
  onRemove,
  fields,
  externalData,
}: {
  index: number;
  control: Control<{ customers: Customer[] }>;
  roleField: RoleFieldMeta;
  productId: string;
  path: string;
  description?: string;
  canRemove: boolean;
  onRemove: () => void;
  fields: Field[];
  externalData?: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const roleValue = useWatch({ control, name: `customers.${index}.role` });
  const roleLabel = roleField?.options.find(o => o.value === roleValue)?.label;
  const cardTitle = t(`${productId}.customerFunnel.${path}.cardTitle`, { defaultValue: '' });
  const title = roleLabel ?? cardTitle;

  return (
    <Card>
      <CardHeader className="pb-4">
        <Group justify="between" gap={0}>
          <div>
            {title && <CardTitle>{title}</CardTitle>}
            {description && (
              <Text size="sm" tone="muted" className="mt-2">
                {description}
              </Text>
            )}
          </div>
          <Group gap={2}>
            {shouldShowAutoFill() && <SectionAutoFillButton fields={fields} />}
            {canRemove && (
              <Button type="button" variant="destructive-ghost" size="icon" onClick={onRemove}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </Group>
        </Group>
      </CardHeader>

      <CardContent>
        <FormBuilderFields fields={fields} externalData={externalData} />
      </CardContent>
    </Card>
  );
}

/**
 * CustomerFunnelModule - Handles customer information in funnel flows
 *
 * Supports two modes:
 * 1. Single-customer mode (default): maxCustomers={1}, minCustomers={1}
 *    - Loads one customer by customerId param
 *    - Uses flat form structure
 *    - Backward compatible with existing flows
 *
 * 2. Multi-customer mode: maxCustomers > 1
 *    - Loads all customers for the policy
 *    - Uses array form structure with cards
 *    - Supports add/remove/edit multiple customers
 *
 * @example Single customer (default)
 * <CustomerFunnelModule sections={sections} />
 *
 * @example Multiple customers
 * <CustomerFunnelModule
 *   sections={sections}
 *   path="customer"
 *   maxCustomers={3}
 *   minCustomers={1}
 * />
 */
export function CustomerFunnelModule({
  sections: rawSections,
  path,
  mode = 'stepper',
  maxCustomers = 1,
  minCustomers = 1,
  title,
  subtitle,
  description,
  role,
  addCustomerButtonLabel: addCustomerButtonLabelProp,
  customResolver,
  afterSubmit,
  helper,
}: CustomerFunnelModuleProps) {
  const { t } = useTranslation();
  const isRoleScoped = !!role;
  const isSingleCustomerMode = !isRoleScoped && maxCustomers === 1;
  const { customerId, policyId, branchId } = useAssertedParams();
  const stepper = useStepperContext();
  const stepperRef = useRef(stepper);
  stepperRef.current = stepper;
  const [customersToDelete, setCustomersToDelete] = useState<string[]>([]);

  if (isSingleCustomerMode && !customerId) {
    throw new Error('customerId is required in single-customer mode');
  }

  const { mutateAsync: updateCustomer } = useCustomerControllerEditCustomer({
    client: { client: clientWithFork },
  });

  const { mutateAsync: createCustomer } = useCustomerControllerCreateCustomerForPolicy({
    client: { client: clientWithFork },
  });

  const { mutateAsync: deleteCustomer } = useCustomerControllerDeleteCustomerFromPolicy({
    client: { client: clientWithFork },
  });

  const {
    data: customer,
    isLoading: isCustomerLoading,
    refetch,
  } = useQuery({
    ...customerControllerGetCustomerQueryOptions(customerId!, { branchId }),
    enabled: isSingleCustomerMode && !!customerId,
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
  });

  const {
    data: existingCustomers,
    isLoading: isCustomersLoading,
    isFetched: isCustomersFetched,
  } = useQuery({
    ...viewSpecificControllerListPolicyCustomersQueryOptions(policyId, {
      branchId,
    }),
    enabled: !isSingleCustomerMode && !!policyId,
    select: data => {
      const customers = data.data.map(customer => ({
        ...customer,
        customFields: keyValueFieldsToObject(customer.customFields ?? []),
      }));
      return role ? customers.filter(c => c.role === role) : customers;
    },
  });

  const { data: productId, isLoading: isPolicyLoading } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId }),
    select: data => data.productId,
  });

  const { sections: resolvedSections, isLoading: isProductConfigLoading } =
    useResolvedEntitySections({ entityType: 'customer', productId, sections: rawSections });

  // FunnelStepHeader owns the step title; the optional `cardTitle` translation
  // becomes the heading inside the form's first Card (used by single-customer
  // mode via SectionedFormBuilder, and by CustomerCard in multi-customer mode).
  const cardTitle = productId
    ? t(`${productId}.customerFunnel.${path}.cardTitle`, { defaultValue: '' })
    : '';
  const sections = useMemo(() => {
    if (resolvedSections.length === 0) return resolvedSections;
    return resolvedSections.map((section, si) => {
      if (si === 0) return { ...section, title: cardTitle || undefined };
      const sectionTitle = productId
        ? t(`${productId}.customerFunnel.${path}.sections.${si}.title`, {
            defaultValue: section.title ?? '',
          })
        : section.title;
      return { ...section, title: sectionTitle || undefined };
    });
  }, [resolvedSections, cardTitle, productId, path, t]);
  const allFields = useMemo(() => buildAllFieldsFromSections(sections), [sections]);

  const roleField = useMemo(() => {
    const field = allFields.find(f => f.name === 'role' && f.type === 'select');
    if (!field || field.type !== 'select') return null;
    const rawOptions = field.options;
    const options = rawOptions.map(o => (typeof o === 'string' ? { value: o, label: o } : o));
    return { options, isSingleOption: options.length === 1 };
  }, [allFields]);

  // The `customer` query may refetch (e.g. after the previous step's mutation
  // invalidates the cache), and each refetch changes the reactive `values`
  // reference, which resets RHF and wipes any field a fast Playwright fill —
  // or a real user typing fast — has just touched. keepDirtyValues preserves
  // user-touched fields across reactive resets.
  const singleCustomerForm = useForm({
    values: buildFormValuesFromFields(allFields, customer),
    resolver: customResolver || getResolver(allFields),
    resetOptions: { keepDirtyValues: true },
  });

  const arrayResolver = useMemo(
    () => getArrayFieldsResolver(allFields, 'customers', minCustomers, maxCustomers),
    [allFields, minCustomers, maxCustomers],
  );

  const multiCustomerForm = useForm<{ customers: Customer[] }>({
    mode: 'all',
    defaultValues: {
      customers: [],
    },
    resolver: arrayResolver,
  });

  const { isDirty: isSingleDirty } = useFormState({ control: singleCustomerForm.control });
  const { isDirty: isMultiDirty } = useFormState({ control: multiCustomerForm.control });
  // biome-ignore lint/correctness/useExhaustiveDependencies: stepperRef.current avoids stale closure without creating a dep cycle
  useEffect(() => {
    if (isSingleDirty || isMultiDirty) stepperRef.current?.invalidateFutureSteps?.();
  }, [isSingleDirty, isMultiDirty]);

  const {
    fields: formFields,
    append,
    remove,
  } = useFieldArray({
    control: multiCustomerForm.control,
    name: 'customers',
    keyName: 'fieldId',
    rules: {
      minLength: minCustomers,
      maxLength: maxCustomers,
    },
  });

  function getCustomerFields(index: number): Field[] {
    return allFields
      .filter(f => !(f.name === 'role' && f.type === 'select' && roleField))
      .map(field => ({
        ...field,
        name: `customers.${index}.${field.name}`,
        showWhen: field.showWhen?.map(condition =>
          condition.fieldName
            ? { ...condition, fieldName: `customers.${index}.${condition.fieldName}` }
            : condition,
        ),
      }));
  }

  const handleAddCustomer = (selectedRole?: string) => {
    if (formFields.length < maxCustomers) {
      const defaults = buildFormValuesFromFields(allFields, {});
      if (role) defaults.role = role;
      else if (selectedRole) defaults.role = selectedRole;
      else if (roleField?.isSingleOption) defaults.role = roleField.options[0].value;
      append(defaults);
      return;
    }
    throw new Error('Max customers reached');
  };

  async function handleRemoveCustomer(index: number) {
    if (formFields.length > minCustomers) {
      const customerData = multiCustomerForm.getValues(`customers.${index}`);

      if (customerData.id) {
        setCustomersToDelete([...customersToDelete, customerData.id]);
      }

      remove(index);
      return;
    }
    throw new Error('Min customers reached');
  }

  useEffect(() => {
    if (!isSingleCustomerMode && isCustomersFetched && allFields.length > 0) {
      const currentCustomerCount = existingCustomers?.length ?? 0;
      const customers: Partial<Customer>[] = [];

      // Always add existing customers first
      if (existingCustomers) {
        for (const customer of existingCustomers) {
          customers.push(
            Object.assign(buildFormValuesFromFields(allFields, customer) as Customer, {
              id: customer.id,
            }),
          );
        }
      }

      // Add empty slots if we're below minCustomers
      if (currentCustomerCount < minCustomers) {
        for (let i = currentCustomerCount; i < minCustomers; i++) {
          const defaults = buildFormValuesFromFields(allFields, {});
          if (role) {
            defaults.role = role;
          } else if (roleField?.isSingleOption) {
            defaults.role = roleField.options[0].value;
          }
          customers.push(defaults);
        }
      }

      multiCustomerForm.reset({ customers });
    }
  }, [
    existingCustomers,
    multiCustomerForm,
    isCustomersFetched,
    minCustomers,
    isSingleCustomerMode,
    allFields,
  ]);

  const onSubmitSingleCustomer = singleCustomerForm.handleSubmit(async data => {
    const { dirtyFields } = singleCustomerForm.formState;
    const isDirty = areFieldsDirty(allFields, dirtyFields);

    let mutationBranchId: string | undefined = branchId;

    if (isDirty) {
      const response = await updateCustomer({
        customerId: customerId!,
        params: { branchId },
        data: buildPayloadFromFields(allFields, data, dirtyFields),
      });

      const newBranchId = getNewBranchIdAfterFork(response);
      if (newBranchId) {
        mutationBranchId = newBranchId;
      }
    }

    refetch();

    if (afterSubmit) {
      const result = await afterSubmit(data, {
        customerId,
        policyId,
        branchId: mutationBranchId,
        isDirty,
      });
      mutationBranchId = result?.branchId ?? mutationBranchId;
    }

    if (mode === 'stepper' && stepper) {
      stepper.next(mutationBranchId ? { branchId: mutationBranchId } : undefined);
    }
  });

  async function handleNextMultiCustomer() {
    const formData = multiCustomerForm.getValues();
    const { dirtyFields } = multiCustomerForm.formState;
    const isDirty = Object.keys(dirtyFields).length > 0 || customersToDelete.length > 0;

    let mutationBranchId: string | undefined = branchId;

    for (const [index, formCustomerData] of formData.customers.entries()) {
      const visibleFields = allFields.filter(field => isFieldVisible(field, formCustomerData));
      const customerDirtyFields = dirtyFields.customers?.[index] ?? {};

      const payload = buildPayloadFromFields(visibleFields, formCustomerData, customerDirtyFields);
      const creationPayload = buildPayloadFromFields(visibleFields, formCustomerData);
      if (formCustomerData.role) {
        creationPayload.role = formCustomerData.role;
      }

      if (formCustomerData.id) {
        if (isEmpty(payload)) {
          continue;
        }

        const response = await updateCustomer({
          customerId: formCustomerData.id,
          params: { branchId: mutationBranchId },
          data: payload,
        });

        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) {
          mutationBranchId = newBranchId;
        }
      } else {
        const response = await createCustomer({
          policyId,
          data: creationPayload as CreateCustomerForPolicyInputBody,
          params: { branchId: mutationBranchId },
        });

        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) {
          mutationBranchId = newBranchId;
        }
      }
    }

    for (const customerId of customersToDelete) {
      const response = await deleteCustomer({
        customerId,
        policyId,
        params: { branchId: mutationBranchId },
      });

      const newBranchId = getNewBranchIdAfterFork(response);
      if (newBranchId) {
        mutationBranchId = newBranchId;
      }
    }

    setCustomersToDelete([]);

    if (afterSubmit) {
      const result = await afterSubmit(formData, {
        customerId,
        policyId,
        branchId: mutationBranchId,
        isDirty,
      });
      mutationBranchId = result?.branchId ?? mutationBranchId;
    }

    if (mode === 'stepper' && stepper) {
      stepper.next(mutationBranchId ? { branchId: mutationBranchId } : undefined);
    }
  }

  const addCustomerButtonLabel = useMemo(() => {
    if (addCustomerButtonLabelProp) return addCustomerButtonLabelProp;
    const roleLabel =
      roleField?.options.find(o => o.value === role)?.label?.toLowerCase() ??
      (role ? t(`relationTypes.${role}.label`)?.toLowerCase() : undefined) ??
      'client';
    return t('customerFunnel.addNewRole', { role: roleLabel });
  }, [addCustomerButtonLabelProp, role, roleField, t]);

  if (isSingleCustomerMode) {
    return (
      <Stack gap={6}>
        <FunnelStepHeader
          title={
            title ??
            t(
              productId
                ? [`${productId}.customerFunnel.${path}.title`, 'funnelSteps.customer.title']
                : 'funnelSteps.customer.title',
            )
          }
          subtitle={subtitle ?? t('funnelSteps.customer.subtitle', '')}
        />
        <FunnelWithHelper helper={helper}>
          <SectionedFormBuilder
            sections={sections}
            form={singleCustomerForm}
            isLoading={isCustomerLoading || isPolicyLoading || isProductConfigLoading}
            externalData={customer}
          >
            <FormBuilderFooter
              mode={mode}
              onSubmit={onSubmitSingleCustomer}
              form={singleCustomerForm}
              showPreviousButton
            />
          </SectionedFormBuilder>
        </FunnelWithHelper>
      </Stack>
    );
  }

  if (isCustomersLoading || isPolicyLoading || isProductConfigLoading) {
    return (
      <div className="grid gap-4">
        <div className="max-w-2xl mx-auto">
          <Center className="h-32">
            <div className="text-sm text-muted-foreground">{t('customerFunnel.loading')}</div>
          </Center>
        </div>
      </div>
    );
  }

  return (
    <Stack gap={6}>
      <FunnelStepHeader
        title={
          title ??
          t(
            productId
              ? [`${productId}.customerFunnel.${path}.title`, 'funnelSteps.customer.title']
              : 'funnelSteps.customer.title',
          )
        }
        subtitle={subtitle ?? t('funnelSteps.customer.subtitle', '')}
      />

      <FunnelWithHelper helper={helper}>
        <Form {...multiCustomerForm}>
          <Stack asChild>
            <form>
              {formFields.map((field, index) => {
                const existingCustomer = existingCustomers?.find(c => c.id === field.id);

                return (
                  <CustomerCard
                    key={field.fieldId}
                    index={index}
                    control={multiCustomerForm.control}
                    roleField={roleField}
                    productId={productId ?? ''}
                    path={path}
                    description={description}
                    canRemove={formFields.length > minCustomers}
                    onRemove={() => handleRemoveCustomer(index)}
                    fields={getCustomerFields(index)}
                    externalData={existingCustomer}
                  />
                );
              })}

              {formFields.length < maxCustomers &&
                (isRoleScoped || !roleField || roleField.isSingleOption ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleAddCustomer()}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {addCustomerButtonLabel}
                  </Button>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" variant="outline" className="w-full">
                        <Plus className="h-4 w-4 mr-2" />
                        {addCustomerButtonLabel}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      {roleField?.options.map(option => (
                        <DropdownMenuItem
                          key={option.value}
                          onClick={() => handleAddCustomer(option.value)}
                        >
                          {option.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ))}
            </form>
          </Stack>
        </Form>
      </FunnelWithHelper>
      <FormBuilderFooter
        mode={mode}
        onSubmit={multiCustomerForm.handleSubmit(async () => await handleNextMultiCustomer())}
        form={multiCustomerForm as UseFormReturn<Record<'customers', unknown>>}
        showPreviousButton
      />
    </Stack>
  );
}
